const PRICE_TABLE: Record<number, number> = {
  1: 399, 2: 798, 3: 999, 4: 1225,
  5: 1440, 6: 1645, 7: 1835, 8: 1999,
};
const OVERFLOW_PER_CARD = 165;

// Total price in cents for a given cart size
export function getTotalPrice(qty: number): number {
  if (qty < 1) return 0;
  if (qty <= 8) return PRICE_TABLE[qty];
  return PRICE_TABLE[8] + (qty - 8) * OVERFLOW_PER_CARD;
}

// Average per-card rate in cents (for badge display only)
export function getPerCardAverage(qty: number): number {
  return qty < 1 ? 399 : Math.round(getTotalPrice(qty) / qty);
}

// Marginal cost of adding one more card (for upsell prompt)
export function getMarginalCost(currentQty: number): number {
  return getTotalPrice(currentQty + 1) - getTotalPrice(currentQty);
}

// Formatted dollar string for the cart total, e.g. "10.00"
export function getFormattedTotal(qty: number): string {
  return (getTotalPrice(qty) / 100).toFixed(2);
}

// ── Promo code types ──────────────────────────────────────────────────────────

export interface PromoCode {
  code: string;
  type: 'per_card' | 'flat' | 'percent' | 'fixed_total';
  cents?: number;           // per_card: discount per card; flat: discount off total; fixed_total: price per group of minQty cards
  pct?: number;             // percent: integer e.g. 25 = 25% off
  minQty?: number;          // fixed_total: cards per group (also the minimum qty to qualify)
  maxUses?: number | null;  // null or omitted = unlimited
}

// Look up a promo code from the PROMO_CODES env var (case-insensitive)
export function findPromo(code: string): PromoCode | null {
  if (!code) return null;
  try {
    const codes: PromoCode[] = JSON.parse(process.env.PROMO_CODES || '[]');
    return codes.find(c => c.code.toLowerCase() === code.toLowerCase()) ?? null;
  } catch {
    return null;
  }
}

// Apply a promo to the full cart total (always operates in total-cents space)
export function applyPromoToTotal(baseTotalCents: number, promo: PromoCode, qty: number): number {
  if (promo.type === 'per_card')    return Math.max(50, baseTotalCents - (promo.cents ?? 0) * qty);
  if (promo.type === 'flat')        return Math.max(50, baseTotalCents - (promo.cents ?? 0));
  if (promo.type === 'percent')     return Math.max(50, Math.round(baseTotalCents * (100 - (promo.pct ?? 0)) / 100));
  if (promo.type === 'fixed_total') {
    // Bundle deal: `cents` buys one group of `minQty` cards. Leftover cards beyond
    // complete groups are billed at the normal rate. E.g. "3 for $5" → 8 cards =
    // 2 groups ($10) + 2 extra cards at normal price ($7.98) = $17.98.
    const groupSize = promo.minQty ?? 3;
    const groups    = Math.floor(qty / groupSize);
    const remainder = qty % groupSize;
    return Math.max(50, groups * (promo.cents ?? 0) + getTotalPrice(remainder));
  }
  return baseTotalCents;
}

// Human-readable label for a promo code
export function promoLabel(promo: PromoCode): string {
  if (promo.type === 'per_card')    return `$${((promo.cents ?? 0) / 100).toFixed(2)} off per card`;
  if (promo.type === 'flat')        return `$${((promo.cents ?? 0) / 100).toFixed(2)} off total`;
  if (promo.type === 'percent')     return `${promo.pct}% off`;
  if (promo.type === 'fixed_total') return `${promo.minQty ?? 3} cards for $${((promo.cents ?? 0) / 100).toFixed(2)}`;
  return 'Discount applied';
}
